import React, { useRef, useState } from 'react';
import { Alert, Box, LinearProgress, Paper, Typography } from '@mui/material';
import CloudUploadIcon from '@mui/icons-material/CloudUpload';
import CheckCircleOutlineIcon from '@mui/icons-material/CheckCircleOutline';

const MAX_FILE_SIZE = 50 * 1024 * 1024; // 50 MB

export interface DropzoneProps {
  status: 'idle' | 'uploading' | 'parsing' | 'done' | 'error';
  errorMessage?: string;
  onFileSelected: (file: File) => void;
  onClearError?: () => void;
  /** Human-readable label for the chosen source (e.g. "Elation Billing-Home" or "athenahealth"). */
  sourceLabel?: string;
}

function isValidFile(file: File): string | null {
  if (!file.name.toLowerCase().endsWith('.csv')) {
    return 'Only .csv files are accepted.';
  }
  if (file.size > MAX_FILE_SIZE) {
    return `File exceeds 50 MB limit (${(file.size / 1024 / 1024).toFixed(1)} MB).`;
  }
  return null;
}

const Dropzone: React.FC<DropzoneProps> = ({
  status,
  errorMessage,
  onFileSelected,
  onClearError,
  sourceLabel = 'Elation Billing-Home',
}) => {
  const inputRef = useRef<HTMLInputElement>(null);
  const [hovering, setHovering] = useState(false);
  const [localError, setLocalError] = useState<string | undefined>();

  const displayError = errorMessage ?? localError;

  const handleClick = () => {
    if (status === 'idle' || status === 'error') {
      inputRef.current?.click();
    }
  };

  const handleInputChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (!file) return;
    // Reset input so re-selecting same file triggers onChange
    e.target.value = '';
    const err = isValidFile(file);
    if (err) {
      setLocalError(err);
      return;
    }
    setLocalError(undefined);
    onFileSelected(file);
  };

  const handleDragOver = (e: React.DragEvent<HTMLDivElement>) => {
    e.preventDefault();
    setHovering(true);
  };

  const handleDragLeave = () => {
    setHovering(false);
  };

  const handleDrop = (e: React.DragEvent<HTMLDivElement>) => {
    e.preventDefault();
    setHovering(false);
    if (status === 'uploading' || status === 'parsing' || status === 'done') return;
    const file = e.dataTransfer.files[0];
    if (!file) return;
    const err = isValidFile(file);
    if (err) {
      setLocalError(err);
      return;
    }
    setLocalError(undefined);
    onFileSelected(file);
  };

  const handleClear = () => {
    setLocalError(undefined);
    onClearError?.();
  };

  const isInteractive = status === 'idle' || status === 'error';

  return (
    <Box>
      {displayError && (
        <Alert severity="error" onClose={handleClear} sx={{ mb: 2 }}>
          {displayError}
        </Alert>
      )}
      <Paper
        data-testid="dropzone"
        variant="outlined"
        onClick={handleClick}
        onDragOver={handleDragOver}
        onDragLeave={handleDragLeave}
        onDrop={handleDrop}
        sx={{
          display: 'flex',
          flexDirection: 'column',
          alignItems: 'center',
          justifyContent: 'center',
          minHeight: 400,
          border: `2px dashed`,
          borderColor: hovering ? 'primary.main' : status === 'done' ? 'success.main' : 'grey.400',
          backgroundColor: hovering ? 'action.hover' : 'background.paper',
          cursor: isInteractive ? 'pointer' : 'default',
          transition: 'border-color 0.2s, background-color 0.2s',
          p: 4,
          gap: 2,
        }}
      >
        <input
          ref={inputRef}
          type="file"
          accept=".csv"
          style={{ display: 'none' }}
          onChange={handleInputChange}
        />

        {(status === 'idle' || status === 'error') && (
          <>
            <CloudUploadIcon sx={{ fontSize: 64, color: 'text.secondary' }} />
            <Typography variant="h6" color="text.secondary" align="center">
              Drop your {sourceLabel} CSV here
            </Typography>
            <Typography variant="body2" color="text.disabled" align="center">
              or click to browse · .csv only · up to 50 MB
            </Typography>
          </>
        )}

        {(status === 'uploading' || status === 'parsing') && (
          <>
            <CloudUploadIcon sx={{ fontSize: 64, color: 'primary.main' }} />
            <Typography variant="h6" color="primary" align="center">
              {status === 'uploading' ? 'Uploading...' : 'Parsing claims...'}
            </Typography>
            <LinearProgress sx={{ width: '60%', mt: 1 }} />
          </>
        )}

        {status === 'done' && (
          <>
            <CheckCircleOutlineIcon sx={{ fontSize: 64, color: 'success.main' }} />
            <Typography variant="h6" color="success.main" align="center">
              Upload successful - redirecting...
            </Typography>
          </>
        )}
      </Paper>
    </Box>
  );
};

export default Dropzone;
